import { pushNotification } from "../pushNotification";

export function workdayRaasForm({
  editToken,
  raasUrl,
  connectionStatus,
  downloadPath,
}) {
  return {
    editToken,
    downloadPath,
    raasUrl,
    testResult: null,
    loading: false,
    saving: false,
    connectionStatus,
    downloading: false,
    exportId: null,
    downloadExportId: null,
    loadingMessages: [
      "Visiting URL...",
      "Authenticating...",
      "Downloading CSV...",
    ],
    currentLoadingMessage: "Testing...",
    loadingMessageIndex: 0,
    loadingInterval: null,
    pollInterval: null,
    downloadPollInterval: null,
    isPolling: false,
    isDownloadPolling: false,

    // Start download process
    async startDownload() {
      this.downloading = true;
      this.downloadExportId = null;
      this.startLoadingMessages();

      try {
        const response = await fetch("/admin/workday-raas/download-csv", {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector("[name=csrf-token]").content,
          },
        });

        if (response.status === 202) {
          const data = await response.json();
          this.downloadExportId = data.export_id;
          this.startDownloadPolling();
          return;
        }

        throw new Error("Failed to start download");
      } catch (error) {
        console.error("Download failed:", error);
        pushNotification({
          title: "Error",
          desc: "Failed to download report. Please try again.",
          success: false,
          position: "top_right",
        });
        this.downloading = false;
        this.stopLoadingMessages();
      }
    },

    // Fetch download status from server
    async fetchDownloadStatus(exportId) {
      const response = await fetch(
        `/admin/workday-raas/poll-download-csv?export_id=${exportId}`,
        {
          method: "GET",
          headers: {
            "X-CSRF-Token": document.querySelector("[name=csrf-token]").content,
          },
        },
      );
      return { response, data: await response.json() };
    },

    // Trigger browser file download
    triggerFileDownload(url, filename) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },

    // Cleanup download polling state
    cleanupDownloadPolling() {
      this.stopDownloadPolling();
      this.stopLoadingMessages();
      this.downloading = false;
      this.isDownloadPolling = false;
    },

    // Handle successful download completion
    handleDownloadSuccess(downloadUrl) {
      this.cleanupDownloadPolling();
      this.triggerFileDownload(downloadUrl, "workday_report.csv");
      pushNotification({
        title: "Success",
        desc: "Report downloaded successfully",
        success: true,
        position: "top_right",
      });
    },

    // Handle download error
    handleDownloadError(error) {
      this.cleanupDownloadPolling();
      pushNotification({
        title: "Download Failed",
        desc: error || "Failed to download report",
        success: false,
        position: "top_right",
      });
    },

    // Poll for download completion
    async pollDownloadCsv() {
      if (!this.downloadExportId || this.isDownloadPolling) return false;

      this.isDownloadPolling = true;

      try {
        const { response, data } = await this.fetchDownloadStatus(
          this.downloadExportId,
        );

        // Still processing
        if (response.status === 202) {
          this.isDownloadPolling = false;
          return false; // Keep polling
        }

        // Success
        if (response.status === 200 && data.success) {
          this.handleDownloadSuccess(data.download_url);
          return true;
        }

        // Error
        if (response.status === 400 || !data.success) {
          this.handleDownloadError(data.error);
          return false;
        }
      } catch (error) {
        console.error("Poll failed:", error);
        this.handleDownloadError("Failed to check download status");
        return false;
      } finally {
        this.isDownloadPolling = false;
      }
    },

    // Start polling for download results
    startDownloadPolling() {
      this.downloadPollInterval = setInterval(() => {
        this.pollDownloadCsv();
      }, 1000); // Poll every 1 second
    },

    // Stop polling for download results
    stopDownloadPolling() {
      if (this.downloadPollInterval) {
        clearInterval(this.downloadPollInterval);
        this.downloadPollInterval = null;
      }
      this.isDownloadPolling = false;
    },

    // Test credentials without saving
    async testCredentials() {
      this.loading = true;
      this.testResult = null;
      this.exportId = null;
      this.startLoadingMessages();

      try {
        await this.testConnection();
        // Keep loading state active while polling - don't set testResult yet
        // It will be set by pollTestCredentials when the job completes
        return true;
      } catch (error) {
        console.error("Test connection failed:", error);
        this.testResult = false;
        this.stopLoadingMessages();
        this.loading = false;
        return false;
      }
    },

    // Start cycling through loading messages
    startLoadingMessages() {
      this.loadingMessageIndex = 0;
      this.currentLoadingMessage =
        this.loadingMessages[this.loadingMessageIndex];
      this.loadingInterval = setInterval(() => {
        if (this.loadingMessageIndex < this.loadingMessages.length - 1) {
          this.loadingMessageIndex++;
          this.currentLoadingMessage =
            this.loadingMessages[this.loadingMessageIndex];
        } else {
          this.stopLoadingMessages(); // Stop the interval if on the last message
        }
      }, 2000); // Change message every 2 seconds
    },

    // Stop cycling through loading messages
    stopLoadingMessages() {
      clearInterval(this.loadingInterval);
    },

    // Poll for test credentials result
    async pollTestCredentials() {
      if (!this.exportId || this.isPolling) return false;

      this.isPolling = true;

      try {
        const response = await fetch(
          `/admin/workday-raas/poll-test-credentials?export_id=${this.exportId}`,
          {
            method: "GET",
            headers: {
              "X-CSRF-Token":
                document.querySelector("[name=csrf-token]").content,
            },
          },
        );

        const data = await response.json();

        // Still processing
        if (response.status === 202) {
          this.isPolling = false;
          return false; // Keep polling
        }

        // Success
        if (response.status === 200 && data.success) {
          this.stopPolling();
          this.stopLoadingMessages();
          this.loading = false;
          this.testResult = true;
          this.connectionStatus = true; // Update the connection status badge
          this.isPolling = false;
          // Update the form field so it gets submitted
          const connectedField = this.$refs.form.querySelector(
            '[name="account[workday_raas_connected]"]',
          );
          if (connectedField) {
            connectedField.value = "true";
          }
          pushNotification({
            title: "Success",
            desc: "Credentials are valid",
            success: true,
            position: "top_right",
          });
          return true;
        }

        // Error
        if (response.status === 400 || !data.success) {
          this.stopPolling();
          this.stopLoadingMessages();
          this.loading = false;
          this.testResult = false;
          this.isPolling = false;
          pushNotification({
            title: "Credential Test Failed",
            desc: data.error || "Failed to verify credentials",
            success: false,
            position: "top_right",
          });
          return false;
        }
      } catch (error) {
        console.error("Poll failed:", error);
        this.stopPolling();
        this.stopLoadingMessages();
        this.loading = false;
        this.testResult = false;
        this.isPolling = false;
        pushNotification({
          title: "Error",
          desc: "Failed to test credentials",
          success: false,
          position: "top_right",
        });
        return false;
      } finally {
        this.isPolling = false;
      }
    },

    // Start polling for test results
    startPolling() {
      this.pollInterval = setInterval(() => {
        this.pollTestCredentials();
      }, 1000); // Poll every 1 second
    },

    // Stop polling
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
      this.isPolling = false;
    },

    // Test connection helper
    async testConnection() {
      const url = document.getElementById("raas-url").value;
      const username = document.getElementById("raas-username").value;
      const password = document.getElementById("raas-password").value;

      try {
        const response = await fetch("/admin/workday-raas/test-credentials", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("[name=csrf-token]").content,
          },
          body: JSON.stringify({ url, username, password }),
        });

        // 202 means the job was queued, not that it succeeded
        if (response.status === 202) {
          const data = await response.json();
          this.exportId = data.export_id;
          this.startPolling();
          return false; // Don't return success yet, need to poll
        }

        return false; // Any other status is not success
      } catch (error) {
        console.error("Connection test failed:", error);
        return false;
      }
    },

    // Handle form submission
    async handleSubmit() {
      this.saving = true;

      try {
        // Create FormData from the form
        const formData = new FormData(this.$refs.form);

        // Use existing connection status if already verified or connected
        formData.set("account[workday_raas_connected]", this.connectionStatus);

        // Submit the form
        const response = await fetch(this.$refs.form.action, {
          method: "POST",
          body: formData,
          headers: {
            "X-CSRF-Token": document.querySelector("[name=csrf-token]").content,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to save settings");
        }

        // Show success message
        pushNotification({
          title: "Settings Saved",
          desc: "Your settings have been saved successfully.",
          success: true,
          position: "top_right",
        });
      } catch (error) {
        console.error("Save failed:", error);
        pushNotification({
          title: "Error",
          desc: "Failed to save settings. Please try again.",
          success: false,
          position: "top_right",
        });
      } finally {
        this.saving = false;
      }
    },
  };
}
